import { Color } from '../math/Color';

/** Horizontal alignment of a text block's lines against each other. */
export const TextAlign = {
  /** Lines share a left edge. */
  Left: 'left',
  /** Lines share a center line. */
  Center: 'center',
  /** Lines share a right edge. */
  Right: 'right',
} as const;

/** One of the {@link TextAlign} options. */
export type TextAlign = (typeof TextAlign)[keyof typeof TextAlign];

/**
 * Everything that decides what a run of text looks like.
 *
 * Plain data with no optional fields, so it is a cache key as much as a style: two calls with equal
 * text and equal style must produce the same texture, and that is only checkable when every field has
 * a value. {@link TextStyleOptions} is the friendly form a caller writes.
 */
export interface TextStyle {
  /** CSS font family list. */
  readonly fontFamily: string;
  /** Cap height in world units, which is also the pixel size before {@link TextStyle.resolution}. */
  readonly fontSize: number;
  /** CSS font weight, such as `400` or `bold`. */
  readonly fontWeight: string;
  /** Fill color of the glyphs. */
  readonly color: Color;
  /** Distance between baselines as a multiple of {@link TextStyle.fontSize}. */
  readonly lineHeight: number;
  /** Space in world units left around the text on all four sides. */
  readonly padding: number;
  /** How lines line up against each other. */
  readonly align: TextAlign;
  /**
   * Pixels rasterized per world unit.
   *
   * Two by default, matching the pixel ratio cap, so text stays crisp on a high density display
   * without the caller doing arithmetic. The drawn sprite is still measured in world units.
   */
  readonly resolution: number;
}

/** A {@link TextStyle} with every field optional. Omitted fields take the engine's default. */
export type TextStyleOptions = Partial<TextStyle>;

const DEFAULT_FONT_SIZE = 48;
const DEFAULT_LINE_HEIGHT = 1.25;
const DEFAULT_RESOLUTION = 2;
const MIN_CANVAS_EXTENT = 1;

/** The style used when a caller gives none, sized for a toddler reading from arm's length. */
export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'system-ui, sans-serif',
  fontSize: DEFAULT_FONT_SIZE,
  fontWeight: 'bold',
  color: Color.white(),
  lineHeight: DEFAULT_LINE_HEIGHT,
  padding: 0,
  align: TextAlign.Center,
  resolution: DEFAULT_RESOLUTION,
};

/** Produces a canvas for rasterizing into; injected so the drawing code is not tied to a document. */
export type CanvasFactory = () => HTMLCanvasElement;

/** Fills in every field a caller left out. */
export function resolveTextStyle(options: TextStyleOptions = {}): TextStyle {
  return { ...DEFAULT_TEXT_STYLE, ...options };
}

/**
 * The asset key a run of text rasterizes to.
 *
 * Deterministic and total: every field that changes a pixel appears in the key, so two identical
 * labels share one texture and two different ones can never collide. The `text:` prefix keeps these
 * keys out of the namespace a game uses for its own files.
 */
export function textTextureKey(text: string, options: TextStyleOptions = {}): string {
  const style = resolveTextStyle(options);
  const fields = [
    style.fontFamily,
    style.fontSize,
    style.fontWeight,
    style.color.toHex(),
    style.lineHeight,
    style.padding,
    style.align,
    style.resolution,
    text,
  ];
  return `text:${fields.join('|')}`;
}

/** The CSS `font` shorthand for a style, as a canvas context wants it. */
export function toCssFont(style: TextStyle, scale = 1): string {
  return `${style.fontWeight} ${String(style.fontSize * scale)}px ${style.fontFamily}`;
}

/**
 * Rasterizes text into a canvas sized to fit it.
 *
 * Lines are split on `\n` and never wrapped: wrapping needs a width the caller has not been asked for,
 * and a label whose line breaks are decided by content data is the more predictable thing to author.
 *
 * The canvas is measured in world units and drawn at {@link TextStyle.resolution} times that, so the
 * sprite that shows it is sized in world units like everything else.
 */
export function createTextCanvas(
  text: string,
  options: TextStyleOptions = {},
  createCanvas: CanvasFactory = defaultCanvasFactory,
): HTMLCanvasElement {
  const style = resolveTextStyle(options);
  const lines = text.split('\n');
  const canvas = createCanvas();

  const context = canvas.getContext('2d');
  if (context === null) {
    throw new Error('Text rasterization needs a 2D canvas context, and this canvas has none.');
  }

  context.font = toCssFont(style);
  const textWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  const lineHeight = style.fontSize * style.lineHeight;
  const worldWidth = textWidth + style.padding * 2;
  const worldHeight = lineHeight * lines.length + style.padding * 2;

  canvas.width = toCanvasExtent(worldWidth * style.resolution);
  canvas.height = toCanvasExtent(worldHeight * style.resolution);

  // Setting width or height resets every context property, so the state is applied after sizing.
  context.scale(style.resolution, style.resolution);
  context.font = toCssFont(style);
  context.textAlign = style.align;
  context.textBaseline = 'middle';
  context.fillStyle = style.color.toHex();

  const originX = alignedOriginX(style, worldWidth);
  for (const [index, line] of lines.entries()) {
    context.fillText(line, originX, style.padding + lineHeight * (index + 0.5));
  }

  return canvas;
}

function alignedOriginX(style: TextStyle, worldWidth: number): number {
  switch (style.align) {
    case TextAlign.Left:
      return style.padding;
    case TextAlign.Right:
      return worldWidth - style.padding;
    case TextAlign.Center:
    default:
      return worldWidth / 2;
  }
}

function toCanvasExtent(value: number): number {
  return Math.max(MIN_CANVAS_EXTENT, Math.ceil(value));
}

function defaultCanvasFactory(): HTMLCanvasElement {
  return document.createElement('canvas');
}
